<script setup>
import { computed, inject, onMounted, ref } from 'vue';
import { activeLinkIndex } from '../navActive.js';
import { sitePrefix } from '../siteLocation.js';
import { initials, styleVars } from './shared.js';
import { menuLinks } from '../menuLinks.js';

const props = defineProps({
    config: { type: Object, required: true },
    accent: { type: String, default: '' },
    org: { type: Object, default: () => ({}) },
    site_data: { type: Object, default: () => ({}) },
});
const editing = inject('tiqEditorCanvas', false);

// The manager's links plus the built-in Trails page (menuLinks.js).
const links = computed(() => menuLinks(props.config.links, props.site_data, editing));
const wordmark = computed(() => props.config.logo_text || props.org?.name || '');
const brandInitials = computed(() => initials(wordmark.value) || 'TQ');
const hasCta = computed(() => String(props.config.cta?.label ?? '').trim() !== '');

// Classic's menu names two style keys Trailhead's shared styleVars does
// not (wordmark, link). Honoured here so a color set under either
// template still lands after a switch.
const vars = computed(() => {
    const s = props.config.style || {};
    const v = { ...styleVars(props.config) };
    if (s.wordmark) v['--text-wordmark'] = s.wordmark;
    if (s.link) v['--text-link'] = s.link;
    return v;
});

// Which nav row is the page the reader is on. Read on mount rather
// than during setup: there is no window while this renders on the
// server, and the editor canvas has a path of its own that means
// nothing here.
const activeIndex = ref(-1);
onMounted(() => { activeIndex.value = activeLinkIndex(links.value, window.location, sitePrefix()); });

// The drawer is the phone fallback. The nav itself scrolls sideways
// down to the width where a row of buttons stops being tappable.
// A link tapped in the drawer may only move the hash rather than
// navigating, which would leave the drawer open over the thing it just
// scrolled to — so every link closes it on the way out.
const drawerOpen = ref(false);
function closeDrawer() { drawerOpen.value = false; }
</script>

<template>
    <header :id="config.anchor_id || null" class="th-block th-header" :style="vars">
        <div class="th-wrap th-header__inner">
            <a href="/" class="th-header__brand">
                <img v-if="org.logo_url" :src="org.logo_url" :alt="org.name || ''" class="th-header__logo">
                <span v-else class="th-header__tile">{{ brandInitials }}</span>
                <span class="th-header__word">{{ wordmark }}</span>
            </a>

            <nav v-if="links.length" class="th-header__nav" aria-label="Site">
                <a v-for="(link, i) in links" :key="i"
                   :href="link.href || '#'"
                   class="th-header__link"
                   :class="{ 'is-active': i === activeIndex }"
                   :aria-current="i === activeIndex ? 'page' : null">{{ link.label }}</a>
            </nav>

            <a v-if="hasCta" :href="config.cta.href || '#'" class="th-btn th-btn--primary th-btn--sm th-header__cta">
                {{ config.cta.label }}
            </a>

            <button v-if="links.length"
                    type="button"
                    class="th-header__burger"
                    :aria-expanded="drawerOpen ? 'true' : 'false'"
                    aria-label="Menu"
                    @click="drawerOpen = !drawerOpen">
                <span></span><span></span><span></span>
            </button>
        </div>

        <div v-if="drawerOpen" class="th-header__drawer">
            <a v-for="(link, i) in links" :key="i"
               :href="link.href || '#'"
               class="th-header__drawer-link"
               :class="{ 'is-active': i === activeIndex }"
               :aria-current="i === activeIndex ? 'page' : null"
               @click="closeDrawer">{{ link.label }}</a>
            <a v-if="hasCta" :href="config.cta.href || '#'"
               class="th-btn th-btn--primary th-btn--block"
               @click="closeDrawer">{{ config.cta.label }}</a>
        </div>
    </header>
</template>

<style scoped>
.th-header {
    position: sticky;
    top: 0;
    z-index: 30;
    background: var(--block-bg, rgba(247, 246, 242, .94));
    -webkit-backdrop-filter: blur(10px);
    backdrop-filter: blur(10px);
    border-bottom: 1px solid var(--th-line);
}
.th-header__inner {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 10px 24px;
    padding-top: 12px;
    padding-bottom: 12px;
}
.th-header__brand {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-right: auto;
    min-width: 0;
    color: var(--text-wordmark, inherit);
    text-decoration: none !important;
}
.th-header__logo { height: 38px; width: auto; max-width: 180px; object-fit: contain; display: block; }
.th-header__tile {
    width: 38px;
    height: 38px;
    flex: none;
    border-radius: var(--r);
    background: var(--accent);
    color: #fff;
    display: grid;
    place-items: center;
    font: 700 14px/1 var(--th-display);
    letter-spacing: .02em;
}
.th-header__word {
    font: 700 17px/1.1 var(--th-display);
    font-stretch: 108%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.th-header__nav {
    display: flex;
    gap: 2px;
    overflow-x: auto;
    max-width: 100%;
    scrollbar-width: none;
}
.th-header__nav::-webkit-scrollbar { display: none; }
.th-header__link {
    padding: 8px 14px;
    border-radius: var(--r);
    font-weight: 600;
    font-size: 15px;
    white-space: nowrap;
    color: var(--text-link, inherit);
    text-decoration: none !important;
}
.th-header__link:hover { background: var(--tint); }
.th-header__link.is-active { background: var(--tint); color: var(--accent); }
.th-header__cta { padding: 10px 18px; font-size: 15px; }

.th-header__burger {
    display: none;
    background: transparent;
    border: 1px solid var(--th-line);
    border-radius: var(--r);
    padding: 9px 8px;
}
.th-header__burger span {
    display: block;
    width: 18px;
    height: 2px;
    background: currentColor;
    border-radius: 2px;
}
.th-header__burger span + span { margin-top: 4px; }
.th-header__drawer {
    display: none;
    flex-direction: column;
    gap: 4px;
    padding: 8px var(--th-gutter) 16px;
    border-top: 1px solid var(--th-line);
}
.th-header__drawer-link {
    padding: 11px 2px;
    font-weight: 600;
    font-size: 16px;
    border-bottom: 1px solid var(--th-line-soft);
    text-decoration: none !important;
}
.th-header__drawer-link.is-active { color: var(--accent); }

/* Below this the row of buttons stops being tappable, so it folds
   into the drawer instead of scrolling sideways. */
@media (max-width: 720px) {
    .th-header__nav { display: none; }
    .th-header__burger { display: block; }
    .th-header__drawer { display: flex; }
    .th-header__cta { display: none; }
}
</style>
